import type { MouseEvent, ReactNode } from 'react';
import Check from '@mui/icons-material/Check';
import { Button, CircularProgress, Stack } from '@mui/material';
import { styled, type Theme } from '@mui/material/styles';
import type { RetainerFieldsFragment } from '~/gql/graphql';
import type { User } from '~/shared/types/connect';
import RetainerCard from '~/modules/matters/RetainerCard';
import { MarkAsClosedLink } from '~/modules/matters/RetainerCard.styles';
import {
  buildReceiptLinesForActiveRetainer,
  formatRetainerRequestAmount,
  retainerCardSubtitle,
  retainerCardTitle,
} from '~/modules/matters/detail/retainersTabUtils';

export interface Props {
  retainer: RetainerFieldsFragment;
  isAttorney: boolean;
  isExpertOnMatter: boolean;
  getExpertById: (id: string) => User | undefined;
  onAddFunds?: () => void;
  markRetainerWorkComplete: (retainerRequestId: string) => Promise<void>;
  /** Retainer ID currently being marked complete; this card shows loading only when it matches its own `retainer.id`. */
  completingRetainerId?: string | null;
  onMarkActive: (requestId: string) => Promise<void>;
  /** Retainer ID currently being marked active; this card shows loading only when it matches its own `retainer.id`. */
  activatingRetainerId?: string | null;
  /** Expert-only: closes a completed retainer; shown as a text link next to "Mark as active". */
  onMarkAsClosed?: (retainerRequestId: string) => void;
  onMessageExpert: (expertId: string) => void;
  hideFooter?: boolean;
  /** Unread message count from this expert; when > 0, a red dot is shown on the Message button. */
  unreadCount?: number;
}

export const ActiveRetainerCard = ({
  retainer,
  isAttorney,
  isExpertOnMatter,
  getExpertById,
  markRetainerWorkComplete,
  completingRetainerId = null,
  onMarkActive,
  activatingRetainerId = null,
  onMarkAsClosed,
  onMessageExpert,
  hideFooter = false,
  unreadCount = 0,
}: Props) => {
  const expertId = retainer.matterExpert.expertId;
  const isCompleted = Boolean(retainer.workCompletedAt);
  const isCompletingThisRetainer = completingRetainerId === retainer.id;
  const isActivatingThisRetainer = activatingRetainerId === retainer.id;
  const showExpertActions = Boolean(!isAttorney && isExpertOnMatter);
  const canExpertMarkWorkComplete = showExpertActions && !isCompleted;
  const expert = getExpertById(expertId);
  const receiptLines = [
    ...buildReceiptLinesForActiveRetainer(
      retainer,
      formatRetainerRequestAmount(retainer),
    ),
    {
      label: 'Status',
      value: isCompleted ? 'Completed' : 'Active',
      ...(isCompleted ? {} : { valueColor: 'success' as const }),
    },
  ];

  const showFooterWithParty = isAttorney && !hideFooter;
  const footerParty = undefined;
  const messageClickHandler =
    isAttorney && expertId ? () => onMessageExpert(expertId) : undefined;

  let primaryActions: ReactNode;
  if (showExpertActions && isCompleted) {
    primaryActions = (
      <CompletedActionsRow>
        <MarkAsActiveButton
          variant="outlined"
          size="medium"
          disabled={isActivatingThisRetainer}
          aria-busy={isActivatingThisRetainer}
          onClick={() => onMarkActive(retainer.id)}
          startIcon={
            isActivatingThisRetainer ? (
              <MarkActiveProgress aria-hidden />
            ) : undefined
          }
        >
          Mark as active
        </MarkAsActiveButton>
        {onMarkAsClosed && (
          <MarkAsClosedLink
            type="button"
            disabled={isActivatingThisRetainer}
            aria-busy={isActivatingThisRetainer}
            onClick={(event: MouseEvent) => {
              event.stopPropagation();
              if (isActivatingThisRetainer) {
                return;
              }
              onMarkAsClosed(retainer.id);
            }}
            aria-label="Mark retainer as closed"
          >
            Mark as closed
          </MarkAsClosedLink>
        )}
      </CompletedActionsRow>
    );
  } else if (canExpertMarkWorkComplete) {
    primaryActions = (
      <MarkAsCompletedButton
        variant="outlined"
        size="medium"
        disabled={isCompletingThisRetainer}
        aria-busy={isCompletingThisRetainer}
        onClick={() => markRetainerWorkComplete(retainer.id)}
        startIcon={
          isCompletingThisRetainer ? (
            <MarkActiveProgress aria-hidden />
          ) : (
            <MarkCompletedCheckIcon aria-hidden />
          )
        }
        aria-label="Mark retainer work complete"
      >
        Mark as completed
      </MarkAsCompletedButton>
    );
  } else {
    primaryActions = undefined;
  }

  return (
    <RetainerCard
      title={retainerCardTitle(retainer)}
      subtitle={retainerCardSubtitle(retainer)}
      expert={isAttorney ? expert ?? undefined : undefined}
      footerParty={footerParty}
      receiptLines={receiptLines}
      expertStatus={
        showFooterWithParty
          ? {
              label: isCompleted ? 'Completed' : 'Active',
              status: isCompleted ? 'Completed' : 'Active',
            }
          : undefined
      }
      primaryActions={primaryActions}
      onMessageClick={messageClickHandler}
      unreadCount={unreadCount}
      hideFooter={!showFooterWithParty}
    />
  );
};

function sharedExpertRetainerActionSizing(theme: Theme) {
  return {
    textTransform: 'none' as const,
    borderRadius: 9999,
    alignSelf: 'flex-start' as const,
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  };
}

const MarkAsActiveButton = styled(Button)(({ theme }) => ({
  ...sharedExpertRetainerActionSizing(theme),
}));

const MarkAsCompletedButton = styled(Button)(({ theme }) => ({
  ...sharedExpertRetainerActionSizing(theme),
  backgroundColor: theme.palette.common.white,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: theme.palette.amount.main,
  color: theme.palette.amount.main,
  fontWeight: 600,
  '&:hover': {
    boxShadow: 'none',
    borderColor: theme.palette.amount.dark,
    color: theme.palette.amount.dark,
    backgroundColor: theme.palette.common.white,
  },
  '&:disabled': {
    borderColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    backgroundColor: theme.palette.common.white,
  },
}));

const MarkCompletedCheckIcon = styled(Check)(({ theme }) => ({
  fontSize: theme.typography.body1.fontSize,
  color: 'inherit',
}));

const MarkActiveProgress = styled(CircularProgress)({
  width: 18,
  height: 18,
});

const CompletedActionsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));
